import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import {
  getDocumentTypes,
  getReceiptTypes,
  getPaymentTypes,
  listOrderSuggestions,
  listChats,
  getChatMessages,
  runOrderSuggestions,
  dismissOrderSuggestion,
  approveOrderSuggestion,
  getRivhitItemPricing,
} from '../../api/client';
import { useRivhitData } from '../../context/RivhitDataContext';
import { useWhatsAppStatus } from '../../hooks/useWhatsAppStatus';
import {
  WizardStep,
  WizardData,
  DocCategory,
  DocType,
  ReceiptType,
  PaymentTypeItem,
  SelectedCustomer,
  SelectedItem,
  PaymentLine,
  CreatedDocument,
  AssistantDocumentPrefill,
  initialWizardData,
} from './types';
import { pickBestCustomerPrice, extractRivhitCustomerId as suggestionCustomerToRivhitId } from '@/lib/pricing';
import DocTypeSelector from './DocTypeSelector';
import CustomerStep from './CustomerStep';
import ItemsStep from './ItemsStep';
import PaymentsStep from './PaymentsStep';
import ReviewStep from './ReviewStep';
import SuccessStep from './SuccessStep';
import ItemReplacementModal from './ItemReplacementModal';

const STEPS_ORDER: WizardStep[] = [
  'select-type',
  'select-customer',
  'select-items',
  'payments',
  'review',
  'success',
];

const STEP_LABELS: Record<WizardStep, string> = {
  'select-type': 'createDocument.steps.selectType',
  'select-customer': 'createDocument.steps.selectCustomer',
  'select-items': 'createDocument.steps.selectItems',
  payments: 'createDocument.steps.payments',
  review: 'createDocument.steps.review',
  success: 'createDocument.steps.success',
};

interface SuggestionItem {
  product_id: string;
  product_name: string;
  quantity: number;
  unit: string;
  price?: number | null;
  catalog_number?: string;
  is_custom?: boolean;
}

interface EditableSuggestionItem extends SuggestionItem {
  draft_id: string;
  item_id?: number;
  catalog_number?: string;
  is_custom?: boolean;
}

interface SuggestionSourceMessage {
  id: string;
  sender: string;
  content: string;
  timestamp: string;
  chat_name: string;
}

interface OrderSuggestion {
  id: string;
  chat_id: string;
  customer_id: string;
  customer_name: string;
  items: SuggestionItem[];
  detected_at: string;
  status: string;
  source_messages?: SuggestionSourceMessage[];
}

interface OrderAnalysisChat {
  id: string;
  name: string;
  is_order_analysis_group?: boolean;
}

interface SuggestionGenerationSummary {
  messagesScanned: number;
  documentsChecked: number;
  missingDocumentsFound: number;
}

interface AnalysisMessage {
  id: string;
  sender_name: string;
  content: string;
  timestamp: string;
  chat_name?: string;
}

const WA_NAME_COLORS = [
  'text-[#1f7aeb]',
  'text-[#d97706]',
  'text-[#6d28d9]',
  'text-[#0f766e]',
  'text-[#be123c]',
  'text-[#4f46e5]',
  'text-[#15803d]',
  'text-[#c2410c]',
];

function formatSuggestionTimestamp(ts: string): string {
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return ts;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatBubbleTime(ts: string): string {
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return '';
  
  const timeStr = date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
  
  const dateStr = date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  
  return `${dateStr}, ${timeStr}`;
}

function getSenderColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return WA_NAME_COLORS[Math.abs(hash) % WA_NAME_COLORS.length];
}

function WhatsAppSidebar({
  chatName,
  messages,
  loading,
  groups,
  onSelectGroup,
  selectedGroupId,
  generating,
  highlightedMessageId,
  onLoadMore,
  hasMore,
  loadingMore,
  waStatus,
  waLoading,
}: {
  chatName: string | null;
  messages: any[];
  loading: boolean;
  groups: OrderAnalysisChat[];
  onSelectGroup: (id: string, name: string) => void;
  selectedGroupId: string | null;
  generating?: boolean;
  highlightedMessageId?: string | null;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  waStatus?: string;
  waLoading?: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const sortedMessages = useMemo(() => [...messages].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  ), [messages]);

  // Scroll to highlighted message during scanning
  useEffect(() => {
    if (highlightedMessageId && scrollRef.current) {
      const el = scrollRef.current.querySelector(`[data-message-id="${highlightedMessageId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [highlightedMessageId]);

  // Infinite scroll: detect scroll to top to load history
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (onLoadMore && hasMore && !loadingMore && !generating) {
      const { scrollTop } = e.currentTarget;
      if (scrollTop < 50) {
        onLoadMore();
      }
    }
  }, [onLoadMore, hasMore, loadingMore, generating]);

  // Maintain scroll position when messages are added to the top
  const lastScrollHeightRef = useRef<number>(0);
  const lastFirstMessageIdRef = useRef<string | null>(null);
  const shouldScrollToBottomRef = useRef<boolean>(true);

  // Reset scroll to bottom flag when chat changes
  useEffect(() => {
    shouldScrollToBottomRef.current = true;
  }, [selectedGroupId]);

  useEffect(() => {
    if (!scrollRef.current) return;

    if (shouldScrollToBottomRef.current && sortedMessages.length > 0 && !loading) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      shouldScrollToBottomRef.current = false;
    } else if (lastFirstMessageIdRef.current) {
      const firstMsg = sortedMessages[0];
      if (firstMsg && (firstMsg.id || firstMsg.timestamp) !== lastFirstMessageIdRef.current) {
        const newScrollHeight = scrollRef.current.scrollHeight;
        const delta = newScrollHeight - lastScrollHeightRef.current;
        scrollRef.current.scrollTop += delta;
      }
    }

    lastScrollHeightRef.current = scrollRef.current.scrollHeight;
    const firstMsg = sortedMessages[0];
    lastFirstMessageIdRef.current = firstMsg ? (firstMsg.id || firstMsg.timestamp) : null;
  }, [sortedMessages, loading]);

  if (waStatus !== 'connected' && !waLoading) {
    return (
      <div className="flex h-[calc(100vh-120px)] flex-col items-center justify-center overflow-hidden rounded-[24px] border border-[#d7dbdd] bg-[#f0f2f5] p-6 text-center shadow-sm lg:sticky lg:top-4">
        <div className="mb-4 text-3xl">📱</div>
        <h3 className="mb-2 text-sm font-bold text-gray-900">WhatsApp Disconnected</h3>
        <p className="mb-6 text-xs text-gray-500">
          Connect WhatsApp to view message context and groups.
        </p>
        <Link
          to="/settings#whatsapp-connection"
          className="rounded-lg bg-[#25D366] px-4 py-2 text-xs font-bold text-white shadow-sm transition-all hover:bg-[#1da851]"
        >
          Connect Now
        </Link>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-120px)] flex-col overflow-hidden rounded-[24px] border border-[#d7dbdd] bg-[#f0f2f5] shadow-sm lg:sticky lg:top-4">
      {/* WhatsApp Header */}
      <div className="flex items-center gap-3 border-b border-[#d7dbdd] bg-[#075e54] px-4 py-3 text-white">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15 text-lg font-semibold">
          {chatName ? chatName.charAt(0) : '#'}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold">{chatName || 'WhatsApp Context'}</h3>
          <p className="text-[10px] text-white/75">
            {generating
              ? 'AI generation in progress'
              : loading
                ? 'Updating...'
                : messages.length > 0
                  ? `${messages.length} messages`
                  : 'Select a group'}
          </p>
        </div>
        {generating && (
          <div className="flex items-center gap-1 rounded-full bg-white/10 px-2 py-1 text-[10px] font-medium text-white/90">
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-emerald-300" />
            Analyzing
          </div>
        )}
      </div>

      {/* Group Selector (if multiple) */}
      {groups.length > 1 && (
        <div className="bg-white/50 px-3 py-2 border-b border-[#d7dbdd]">
          <select
            value={selectedGroupId || ''}
            onChange={(e) => {
              const g = groups.find((group) => group.id === e.target.value);
              if (g) onSelectGroup(g.id, g.name);
            }}
            className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700 outline-none focus:border-emerald-400"
          >
            <option value="" disabled>Select monitored group...</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Messages */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex flex-1 flex-col gap-2 overflow-y-auto p-4 scrollbar-thin"
        style={{
          backgroundColor: '#efeae2',
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'64\' height=\'64\' viewBox=\'0 0 64 64\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23cfd6da\' fill-opacity=\'.28\'%3E%3Cpath d=\'M36 34h-4v4h4v-4zm0-30h-4v4h4V4zM6 34H2v4h4v-4zM6 4H2v4h4V4zm30 30h-2v2h2v-2zm0-30h-2v2h2V4zM6 34H4v2h2v-2zm0-30H4v2h2V4z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        }}
      >
        {loading && sortedMessages.length === 0 ? (
          <div className="m-auto text-center">
            <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
            <p className="mt-2 text-xs text-gray-500">Loading history...</p>
          </div>
        ) : sortedMessages.length === 0 ? (
          <div className="m-auto max-w-[180px] rounded-2xl bg-white/80 px-3 py-4 text-center text-xs text-gray-500 shadow-sm">
            {selectedGroupId ? 'No messages found for the selected group.' : 'Select a monitored group to view its conversation history.'}
          </div>
        ) : (
          <>
            {loadingMore && (
              <div className="py-2 text-center">
                <div className="mx-auto h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
              </div>
            )}
            {sortedMessages.map((message) => {
              const messageId = message.id || `${message.sender || message.sender_name}-${message.timestamp}`;
              const isHighlighted = highlightedMessageId === message.id;
              return (
                <div key={messageId} className="flex justify-start">
                  <div
                    data-message-id={messageId}
                    className={`relative max-w-[90%] rounded-[12px] rounded-tl-[4px] px-3 py-1.5 shadow-sm transition ${
                      isHighlighted ? 'bg-[#dcf8c6] ring-2 ring-emerald-300' : 'bg-white'
                    }`}
                  >
                    <div
                      className={`absolute left-[-6px] top-0 h-3 w-3 ${isHighlighted ? 'bg-[#dcf8c6]' : 'bg-white'}`}
                      style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }}
                    />
                    <p className={`mb-0.5 text-[10px] font-bold ${getSenderColor(message.sender || message.sender_name)}`}>
                      {message.sender || message.sender_name}
                    </p>
                    <p className="whitespace-pre-wrap break-words text-[12px] leading-snug text-gray-900">
                      {message.content}
                    </p>
                    <div className="mt-1 flex justify-end">
                      <span className="text-[9px] text-gray-400">{formatBubbleTime(message.timestamp)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}


function normalizeItemName(value: string | undefined | null): string {
  return (value ?? '').trim().toLowerCase();
}

function buildEditableSuggestionItems(
  items: SuggestionItem[],
  rivhitItems: Array<{ item_id?: number; item_name?: string; catalog_number?: string; quantity_type?: string; price?: number }>,
): EditableSuggestionItem[] {
  return items.map((item) => {
    const normalizedName = normalizeItemName(item.product_name);
    // Find item by name or catalog number if provided in the suggestion
    const matchedRivhitItem = rivhitItems.find(
      (candidate) =>
        normalizeItemName(candidate.item_name) === normalizedName ||
        (item.catalog_number && candidate.catalog_number === item.catalog_number)
    );

    return {
      draft_id: crypto.randomUUID(),
      product_id: matchedRivhitItem ? `item-${matchedRivhitItem.item_id}` : item.product_id,
      product_name: matchedRivhitItem?.item_name || item.product_name,
      quantity: item.quantity,
      unit: matchedRivhitItem?.quantity_type || item.unit || '',
      price: item.price ?? matchedRivhitItem?.price ?? null,
      item_id: matchedRivhitItem?.item_id,
      catalog_number: matchedRivhitItem?.catalog_number,
      is_custom: !matchedRivhitItem,
    };
  });
}

function SuggestionItemEditor({
  items,
  rivhitItems,
  customerId,
  onChange,
}: {
  items: EditableSuggestionItem[];
  rivhitItems: typeof useRivhitData extends () => infer T ? T extends { items: infer I } ? I : never : never;
  customerId: string;
  onChange: (items: EditableSuggestionItem[]) => void;
}) {
  const [replacingItemId, setReplacingItemId] = useState<string | null>(null);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [fetchingPriceId, setFetchingPriceId] = useState<string | null>(null);

  const replacingItem = useMemo(() => {
    return items.find((item) => item.draft_id === replacingItemId);
  }, [items, replacingItemId]);

  const updateDraftItem = async (draftId: string, patch: Partial<EditableSuggestionItem>) => {
    const item = items.find((it) => it.draft_id === draftId);
    if (!item) return;

    let updatedPatch = { ...patch };

    // If quantity changed and it's a catalog item, re-resolve price
    if (patch.quantity !== undefined && item.item_id) {
      const rivhitCustomerId = suggestionCustomerToRivhitId(customerId);
      if (rivhitCustomerId) {
        try {
          const pricingRes = await getRivhitItemPricing(item.item_id, rivhitCustomerId);
          if (pricingRes.data && Array.isArray(pricingRes.data)) {
            updatedPatch.price = pickBestCustomerPrice(pricingRes.data, patch.quantity, item.price ?? 0);
          }
        } catch (err) {
          console.warn('Failed to re-resolve price on quantity change:', err);
        }
      }
    }

    onChange(items.map((it) => (it.draft_id === draftId ? { ...it, ...updatedPatch } : it)));
  };

  const removeDraftItem = (draftId: string) => {
    onChange(items.filter((item) => item.draft_id !== draftId));
  };

  const handleItemSelect = async (rivhitItem: any, targetDraftId: string | null) => {
    // Attempt to get the customer-specific price
    let finalPrice = rivhitItem.price ?? null;
    const rivhitCustomerId = suggestionCustomerToRivhitId(customerId);
    
    if (rivhitCustomerId && rivhitItem.item_id) {
      if (targetDraftId) setFetchingPriceId(targetDraftId);
      try {
        const pricingRes = await getRivhitItemPricing(rivhitItem.item_id, rivhitCustomerId);
        if (pricingRes.data && Array.isArray(pricingRes.data)) {
          // If replacing, use current quantity. If adding, use 1.
          const qty = targetDraftId ? (items.find(i => i.draft_id === targetDraftId)?.quantity || 1) : 1;
          finalPrice = pickBestCustomerPrice(pricingRes.data, qty, finalPrice);
        }
      } catch (err) {
        console.warn('Failed to fetch pricing, using default:', err);
      } finally {
        if (targetDraftId) setFetchingPriceId(null);
      }
    }

    if (targetDraftId) {
      // Replace existing
      onChange(
        items.map((item) =>
          item.draft_id === targetDraftId
            ? {
                ...item,
                product_id: `item-${rivhitItem.item_id ?? crypto.randomUUID()}`,
                product_name: rivhitItem.item_name || 'Unnamed item',
                unit: rivhitItem.quantity_type || '',
                price: finalPrice,
                item_id: rivhitItem.item_id,
                catalog_number: rivhitItem.catalog_number,
                is_custom: false,
              }
            : item
        )
      );
    } else {
      // Add new
      onChange([
        ...items,
        {
          draft_id: crypto.randomUUID(),
          product_id: `item-${rivhitItem.item_id ?? crypto.randomUUID()}`,
          product_name: rivhitItem.item_name || 'Unnamed item',
          quantity: 1,
          unit: rivhitItem.quantity_type || '',
          price: finalPrice,
          item_id: rivhitItem.item_id,
          catalog_number: rivhitItem.catalog_number,
          is_custom: false,
        },
      ]);
    }
    setReplacingItemId(null);
    setIsAddingItem(false);
  };

  return (
    <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50/50 p-3" onClick={(event) => event.stopPropagation()}>
      <div className="mb-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Edit suggested items</p>
        <p className="mt-1 text-xs text-blue-900/70">Ensure all items are from the catalog. Replace custom items and adjust quantities.</p>
      </div>

      <div className="space-y-2">
        {items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-blue-200 bg-white/80 px-3 py-4 text-sm text-gray-500">
            No items selected yet.
          </div>
        ) : (
          items.map((item) => (
            <div key={item.draft_id} className={`rounded-xl border bg-white px-3 py-3 shadow-sm transition ${item.is_custom ? 'border-amber-200 bg-amber-50/30' : 'border-white'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <input
                    type="text"
                    value={item.product_name}
                    onChange={(event) =>
                      updateDraftItem(item.draft_id, {
                        product_name: event.target.value,
                      })
                    }
                    className="w-full rounded-lg border border-transparent bg-transparent px-0 py-0.5 text-sm font-medium text-gray-900 outline-none transition focus:border-blue-400 focus:bg-white focus:px-2"
                  />
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-xs text-gray-500">
                      {[item.catalog_number && `#${item.catalog_number}`, item.unit].filter(Boolean).join(' • ') || 'Custom item'}
                    </p>
                    {item.is_custom && (
                      <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700 uppercase">Unmapped</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setReplacingItemId(item.draft_id)}
                    className="rounded-lg px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 flex items-center gap-1"
                  >
                    {fetchingPriceId === item.draft_id ? (
                      <span className="h-3 w-3 animate-spin rounded-full border border-blue-600 border-t-transparent" />
                    ) : 'Edit'}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeDraftItem(item.draft_id)}
                    className="rounded-lg px-2 py-1 text-xs font-medium text-gray-400 hover:bg-red-50 hover:text-red-600"
                  >
                    Remove
                  </button>
                </div>
              </div>

              <div className="mt-3 flex items-center gap-3">
                <div className="relative flex-1">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400 uppercase">Qty</span>
                  <input
                    type="number"
                    min={0.01}
                    step={0.01}
                    value={item.quantity}
                    onChange={(event) =>
                      updateDraftItem(item.draft_id, {
                        quantity: parseFloat(event.target.value) || 0,
                      })
                    }
                    className="w-full rounded-lg border border-gray-200 bg-gray-50/50 pl-10 pr-3 py-1.5 text-sm text-gray-800 outline-none transition focus:border-blue-400 focus:bg-white"
                  />
                </div>
                <div className="relative flex-1">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400">₪</span>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={item.price ?? ''}
                    onChange={(event) =>
                      updateDraftItem(item.draft_id, {
                        price: parseFloat(event.target.value) || 0,
                      })
                    }
                    className="w-full rounded-lg border border-gray-200 bg-gray-50/50 pl-6 pr-3 py-1.5 text-sm font-bold text-blue-700 outline-none transition focus:border-blue-400 focus:bg-white"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-4">
        <button
          type="button"
          onClick={() => setIsAddingItem(true)}
          className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-blue-200 bg-white/50 px-4 py-3 text-sm font-medium text-blue-600 transition hover:border-blue-300 hover:bg-white hover:text-blue-700"
        >
          <span className="text-lg">+</span> Add Item from Catalog
        </button>
      </div>

      {(replacingItemId || isAddingItem) && (
        <ItemReplacementModal
          isOpen={!!replacingItemId || isAddingItem}
          onClose={() => {
            setReplacingItemId(null);
            setIsAddingItem(false);
          }}
          onSelect={(rivhitItem) => handleItemSelect(rivhitItem, replacingItemId)}
          items={rivhitItems}
          currentItemName={replacingItem?.product_name}
        />
      )}
    </div>
  );
}

export default function CreateDocument() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { status: waStatus, loading: waLoading } = useWhatsAppStatus();
  const {
    configured,
    customers,
    customersLoading,
    ensureCustomersLoaded,
    items: rivhitItems,
    itemsLoading: rivhitItemsLoading,
    ensureItemsLoaded,
  } = useRivhitData();
  const [step, setStep] = useState<WizardStep>('select-type');
  const [data, setData] = useState<WizardData>({ ...initialWizardData });

  // Reference data
  const [docTypes, setDocTypes] = useState<DocType[]>([]);
  const [receiptTypes, setReceiptTypes] = useState<ReceiptType[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<PaymentTypeItem[]>([]);
  const [refDataLoading, setRefDataLoading] = useState(true);
  const [suggestions, setSuggestions] = useState<OrderSuggestion[]>([]);
  const [suggestionDrafts, setSuggestionDrafts] = useState<Record<string, EditableSuggestionItem[]>>({});
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);
  const [activeSuggestionId, setActiveSuggestionId] = useState<string | null>(null);
  const [actingSuggestionId, setActingSuggestionId] = useState<string | null>(null);
  const [generatingSuggestions, setGeneratingSuggestions] = useState(false);
  const [generateSuggestionsError, setGenerateSuggestionsError] = useState<string | null>(null);
  const [generationSummary, setGenerationSummary] = useState<SuggestionGenerationSummary | null>(null);
  const [analysisMessages, setAnalysisMessages] = useState<AnalysisMessage[]>([]);
  const [highlightedAnalysisMessageId, setHighlightedAnalysisMessageId] = useState<string | null>(null);
  const suggestionsSectionRef = useRef<HTMLElement | null>(null);
  const isRtl = i18n.dir() === 'rtl';
  const appliedPrefillKeyRef = useRef<string | null>(null);

  // WhatsApp Sidebar State
  const [monitoredGroups, setMonitoredGroups] = useState<OrderAnalysisChat[]>([]);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [selectedChatName, setSelectedChatName] = useState<string | null>(null);
  const [selectedChatMessages, setSelectedChatMessages] = useState<AnalysisMessage[]>([]);
  const [fetchingMessages, setFetchingMessages] = useState(false);
  const [fetchingMoreMessages, setFetchingMoreMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);

  const loadMoreMessages = useCallback(async () => {
    if (!selectedChatId || fetchingMoreMessages || !hasMoreMessages) return;

    setFetchingMoreMessages(true);
    try {
      // Find the oldest message timestamp we currently have
      const oldestTs = selectedChatMessages.length > 0
        ? selectedChatMessages.reduce((min, m) => (m.timestamp < min ? m.timestamp : min), selectedChatMessages[0].timestamp)
        : new Date().toISOString();

      const res = await getChatMessages(selectedChatId, { limit: 50, to_timestamp: oldestTs });
      const newMessages = Array.isArray(res.data) ? res.data : res.data?.items ?? [];
      
      if (newMessages.length === 0) {
        setHasMoreMessages(false);
      } else {
        setSelectedChatMessages((prev) => {
          // Filter out duplicates just in case
          const existingIds = new Set(prev.map((m) => m.id));
          const uniqueNew = newMessages.filter((m: any) => !existingIds.has(m.id));
          return [...prev, ...uniqueNew];
        });
        if (newMessages.length < 50) {
          setHasMoreMessages(false);
        }
      }
    } catch (err) {
      console.error('Failed to load more messages:', err);
    } finally {
      setFetchingMoreMessages(false);
    }
  }, [selectedChatId, fetchingMoreMessages, hasMoreMessages, selectedChatMessages]);

  const resolveCategoryDefaults = useCallback((
    category: DocCategory,
    availableDocTypes: DocType[],
    availableReceiptTypes: ReceiptType[],
  ) => {
    let docType: number | null = null;
    let receiptType: number | null = null;
    let priceIncludeVat = true;

    if (category === 'receipt') {
      const defaultReceipt = availableReceiptTypes[0];
      receiptType = defaultReceipt?.receipt_type ?? null;
    } else {
      const filtered = availableDocTypes.filter((dt) => {
        if (category === 'invoice') {
          return dt.is_accounting && !dt.document_name.toLowerCase().includes('delivery');
        }
        if (category === 'delivery_note') {
          return dt.document_name.toLowerCase().includes('delivery') || dt.document_name.includes('תעודת משלוח');
        }
        return false;
      });
      const chosen = filtered[0] || availableDocTypes[0];
      docType = chosen?.document_type ?? null;
      priceIncludeVat = chosen?.price_include_vat ?? true;
    }

    return {
      docType,
      receiptType,
      priceIncludeVat,
    };
  }, []);

  const loadRefData = useCallback(async () => {
    try {
      const [dtRes, rtRes, ptRes] = await Promise.all([
        getDocumentTypes(),
        getReceiptTypes(),
        getPaymentTypes(),
      ]);
      setDocTypes(dtRes.data ?? []);
      setReceiptTypes(
        (rtRes.data ?? []).filter((rt: ReceiptType) => !rt.is_invoice_receipt),
      );
      setPaymentTypes(ptRes.data ?? []);
    } catch {
      // non-critical — forms still work with empty ref data
    } finally {
      setRefDataLoading(false);
    }
  }, []);

  const loadSuggestions = useCallback(async (isSilent = false) => {
    if (!isSilent) setSuggestionsLoading(true);
    try {
      const response = await listOrderSuggestions(10);
      const nextSuggestions = response.data ?? [];
      setSuggestions(nextSuggestions);
      setSuggestionDrafts(
        Object.fromEntries(
          nextSuggestions.map((suggestion: OrderSuggestion) => [
            suggestion.id,
            buildEditableSuggestionItems(suggestion.items, rivhitItems),
          ]),
        ),
      );
    } catch {
      setSuggestions([]);
      setSuggestionDrafts({});
    } finally {
      if (!isSilent) setSuggestionsLoading(false);
    }
  }, [rivhitItems]);

  useEffect(() => {
    loadRefData();
    loadSuggestions();

    // Load monitored groups for sidebar selection
    listChats().then((res) => {
      const all = Array.isArray(res.data) ? res.data : res.data?.items ?? [];
      const groups = all.filter((c: any) => c.is_order_analysis_group);
      setMonitoredGroups(groups);
      if (!selectedChatId && groups.length > 0) {
        setSelectedChatId(groups[0].id);
        setSelectedChatName(groups[0].name || 'WhatsApp Group');
      }
    });
  }, [loadRefData, loadSuggestions, selectedChatId]);

  // Fetch messages when selected chat changes
  useEffect(() => {
    let cancelled = false;

    if (!selectedChatId) {
      setSelectedChatMessages([]);
      return;
    }

    // If we have messages from the active suggestion, use them
    const activeSuggestion = suggestions.find((s) => s.id === activeSuggestionId);
    if (activeSuggestion && activeSuggestion.chat_id === selectedChatId && activeSuggestion.source_messages) {
      setSelectedChatMessages(
        activeSuggestion.source_messages.map((m) => ({
          id: m.id,
          sender_name: m.sender,
          content: m.content,
          timestamp: m.timestamp,
        }))
      );
      return;
    }

    // Otherwise fetch fresh messages
    setSelectedChatMessages([]);
    setFetchingMessages(true);
    setHasMoreMessages(true);
    const sinceTimestamp = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    getChatMessages(selectedChatId, { limit: 100, from_timestamp: sinceTimestamp })
      .then((res) => {
        if (cancelled) return;
        const messages = Array.isArray(res.data) ? res.data : res.data?.items ?? [];
        setSelectedChatMessages(messages);
        if (messages.length < 100) {
          // If we got fewer than 100 messages from the last 24h, 
          // we might still have more history in the DB.
          // But for now let's just keep hasMoreMessages=true to allow scrolling for more.
        }
      })
      .catch(() => {
        if (!cancelled) setSelectedChatMessages([]);
      })
      .finally(() => {
        if (!cancelled) setFetchingMessages(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedChatId, activeSuggestionId, suggestions]);

  useEffect(() => {
    if (!generatingSuggestions || analysisMessages.length === 0) {
      setHighlightedAnalysisMessageId(null);
      return;
    }
    let index = 0;
    setHighlightedAnalysisMessageId(analysisMessages[0]?.id ?? null);
    const interval = window.setInterval(() => {
      index = (index + 1) % analysisMessages.length;
      setHighlightedAnalysisMessageId(analysisMessages[index]?.id ?? null);
    }, 700);
    return () => window.clearInterval(interval);
  }, [analysisMessages, generatingSuggestions]);

  useEffect(() => {
    if (!configured) return;
    ensureCustomersLoaded();
    ensureItemsLoaded();
  }, [configured, ensureCustomersLoaded, ensureItemsLoaded]);

  useEffect(() => {
    if (!data.category) return;

    const nextDefaults = resolveCategoryDefaults(data.category, docTypes, receiptTypes);
    if (data.category === 'receipt') {
      if (data.receiptType || !nextDefaults.receiptType) return;
    } else {
      if (data.docType || !nextDefaults.docType) return;
    }

    setData((prev) => ({
      ...prev,
      ...nextDefaults,
    }));
  }, [data.category, data.docType, data.receiptType, docTypes, receiptTypes, resolveCategoryDefaults]);

  useEffect(() => {
    const assistantPrefill = (location.state as { assistantPrefill?: AssistantDocumentPrefill } | null)?.assistantPrefill;
    if (!assistantPrefill || refDataLoading) return;

    const prefillKey = JSON.stringify(assistantPrefill);
    if (appliedPrefillKeyRef.current === prefillKey) return;
    appliedPrefillKeyRef.current = prefillKey;

    const category = assistantPrefill.category ?? 'invoice';
    const defaults = resolveCategoryDefaults(category, docTypes, receiptTypes);
    const selectedCustomer = assistantPrefill.customer
      ? {
          customer_id: assistantPrefill.customer.customer_id ?? null,
          display_name: assistantPrefill.customer.display_name,
          is_free_text: assistantPrefill.customer.is_free_text ?? assistantPrefill.customer.customer_id == null,
        }
      : null;
    const selectedItems: SelectedItem[] = (assistantPrefill.items ?? []).map((item) => ({
      id: crypto.randomUUID(),
      item_id: item.item_id,
      catalog_number: item.catalog_number,
      description: item.description,
      quantity: item.quantity,
      price: item.price ?? 0,
      unit: item.unit || '',
      is_custom: item.is_custom ?? item.item_id == null,
    }));

    setActiveSuggestionId(null);
    setData((prev) => ({
      ...prev,
      category,
      docType: category === 'receipt' ? defaults.docType : defaults.docType,
      receiptType: category === 'receipt' ? defaults.receiptType : null,
      priceIncludeVat: defaults.priceIncludeVat,
      customer: selectedCustomer,
      items: selectedItems,
      comments: assistantPrefill.comments ?? '',
      payments: category === 'receipt' ? prev.payments : [],
      createdDoc: null,
    }));

    if (!selectedCustomer) {
      setStep('select-customer');
    } else if ((assistantPrefill.items ?? []).length === 0 && category !== 'receipt') {
      setStep('select-items');
    } else {
      setStep('review');
    }
  }, [location.state, refDataLoading, resolveCategoryDefaults, docTypes, receiptTypes]);

  // Derive visible steps (skip payments for non-receipt)
  const visibleSteps = STEPS_ORDER.filter(
    (s) => s !== 'payments' || data.category === 'receipt',
  );

  const currentIndex = visibleSteps.indexOf(step);

  const canGoNext = (): boolean => {
    switch (step) {
      case 'select-type':
        return data.category !== null && (data.docType !== null || data.receiptType !== null);
      case 'select-customer':
        return data.customer !== null && data.customer.display_name.trim().length > 0;
      case 'select-items':
        return data.category === 'receipt' || data.items.length > 0;
      case 'payments':
        return data.payments.length > 0 && data.payments.every((p) => p.amount > 0);
      default:
        return false;
    }
  };

  const goNext = () => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < visibleSteps.length) {
      setStep(visibleSteps[nextIndex]);
    }
  };

  const goBack = () => {
    const prevIndex = currentIndex - 1;
    if (prevIndex >= 0) {
      setStep(visibleSteps[prevIndex]);
    }
  };

  const resetWizard = () => {
    setData({ ...initialWizardData });
    setStep('select-type');
  };

  // Map category selection to default Rivhit doc/receipt types
  const handleCategorySelect = (category: DocCategory) => {
    const nextDefaults = resolveCategoryDefaults(category, docTypes, receiptTypes);
    const typesReady = category === 'receipt'
      ? nextDefaults.receiptType !== null
      : nextDefaults.docType !== null;

    if (!typesReady) {
      return;
    }

    setData((prev) => ({
      ...prev,
      category,
      ...nextDefaults,
    }));

    // Auto-advance to next step on doc type selection
    setStep('select-customer');
  };

  const handleCustomerSelect = (customer: SelectedCustomer) => {
    setData((prev) => ({ ...prev, customer }));
  };

  const handleItemsChange = (items: SelectedItem[]) => {
    setData((prev) => ({ ...prev, items }));
  };

  const handlePaymentsChange = (payments: PaymentLine[]) => {
    setData((prev) => ({ ...prev, payments }));
  };

  const handleCommentsChange = (comments: string) => {
    setData((prev) => ({ ...prev, comments }));
  };

  const handleSuccess = async (doc: CreatedDocument) => {
    if (activeSuggestionId) {
      try {
        await approveOrderSuggestion(activeSuggestionId, {
          document_type: doc.document_type,
          document_number: doc.document_number,
          document_link: doc.document_link,
          customer_id: doc.customer_id ?? null,
        });
      } catch {
        // Keep the document success UX even if suggestion finalization fails.
      }
    }
    setData((prev) => ({ ...prev, createdDoc: doc }));
    setActiveSuggestionId(null);
    loadSuggestions(true);
    setStep('success');
  };

  const applySuggestion = useCallback((suggestion: OrderSuggestion) => {
    const defaults = resolveCategoryDefaults('invoice', docTypes, receiptTypes);
    const rivhitCustomerId = suggestionCustomerToRivhitId(suggestion.customer_id);
    const rivhitCustomer = rivhitCustomerId != null
      ? customers.find((customer) => customer.customer_id === rivhitCustomerId)
      : undefined;
    const selectedCustomer: SelectedCustomer = {
      customer_id: rivhitCustomer?.customer_id ?? rivhitCustomerId,
      display_name: rivhitCustomer ? `${rivhitCustomer.first_name} ${rivhitCustomer.last_name}`.trim() : suggestion.customer_name,
      is_free_text: !rivhitCustomer,
    };
    const draftItems = suggestionDrafts[suggestion.id] ?? buildEditableSuggestionItems(suggestion.items, rivhitItems);
    const selectedItems: SelectedItem[] = draftItems.map((item) => {
      const rivhitItem = rivhitItems.find((candidate) =>
        candidate.item_id != null && item.item_id != null
          ? candidate.item_id === item.item_id
          : candidate.item_name?.trim().toLowerCase() === item.product_name.trim().toLowerCase()
      );
      return {
        id: crypto.randomUUID(),
        item_id: item.item_id ?? rivhitItem?.item_id,
        catalog_number: item.catalog_number ?? rivhitItem?.catalog_number,
        description: item.product_name,
        quantity: item.quantity,
        price: item.price ?? rivhitItem?.price ?? 0,
        unit: item.unit || rivhitItem?.quantity_type || '',
        is_custom: item.is_custom ?? !rivhitItem,
      };
    });

    setActiveSuggestionId(suggestion.id);
    setSelectedChatId(suggestion.chat_id);
    const firstMsg = suggestion.source_messages?.[0];
    if (firstMsg?.chat_name) {
      setSelectedChatName(firstMsg.chat_name);
    } else {
      const group = monitoredGroups.find((g) => g.id === suggestion.chat_id);
      setSelectedChatName(group?.name || 'WhatsApp Group');
    }

    setData((prev) => ({
      ...prev,
      category: 'invoice',
      docType: defaults.docType,
      receiptType: defaults.receiptType,
      priceIncludeVat: defaults.priceIncludeVat,
      customer: selectedCustomer,
      items: selectedItems,
      payments: [],
      comments: prev.comments,
      createdDoc: null,
    }));
    setStep('select-items');
    requestAnimationFrame(() => {
      suggestionsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [customers, docTypes, receiptTypes, resolveCategoryDefaults, rivhitItems, suggestionDrafts, monitoredGroups]);

  const handleDismissSuggestion = useCallback(async (suggestionId: string) => {
    setActingSuggestionId(suggestionId);
    try {
      await dismissOrderSuggestion(suggestionId);
      await loadSuggestions(true);
      if (activeSuggestionId === suggestionId) {
        setActiveSuggestionId(null);
      }
    } finally {
      setActingSuggestionId(null);
    }
  }, [activeSuggestionId, loadSuggestions]);

  const removeDraftItemAt = useCallback((suggestionId: string, index: number) => {
    setSuggestionDrafts((current) => {
      const draft = current[suggestionId];
      if (!draft) return current;
      const next = [...draft];
      next.splice(index, 1);
      return { ...current, [suggestionId]: next };
    });
  }, []);

  const handleGenerateSuggestions = useCallback(async () => {
    setGeneratingSuggestions(true);
    setGenerateSuggestionsError(null);
    setGenerationSummary(null);
    setAnalysisMessages([]);
    try {
      const chatsResponse = await listChats();
      const chats = Array.isArray(chatsResponse.data)
        ? chatsResponse.data
        : chatsResponse.data?.items ?? chatsResponse.data?.chats ?? [];
      const orderAnalysisChats = (chats as OrderAnalysisChat[]).filter((chat) => chat.is_order_analysis_group);

      if (orderAnalysisChats.length === 0) {
        setGenerateSuggestionsError('No chat is marked as an order analysis group in Settings.');
        return;
      }

      const sinceTimestamp = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const messageResponses = await Promise.all(
        orderAnalysisChats.map(async (chat) => {
          const response = await getChatMessages(chat.id, { limit: 1000, from_timestamp: sinceTimestamp });
          const messages = Array.isArray(response.data) ? response.data : response.data?.items ?? [];
          return messages.map((message: any) => ({
            id: message.id,
            sender_name: message.sender_name ?? 'Unknown',
            content: message.content ?? '',
            timestamp: message.timestamp,
            chat_name: message.chat_name,
          }));
        })
      );
      const orderedMessages = messageResponses
        .flat()
        .filter((message) => message.content?.trim())
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      setAnalysisMessages(orderedMessages);

      const summary: SuggestionGenerationSummary = {
        messagesScanned: 0,
        documentsChecked: 0,
        missingDocumentsFound: 0,
      };
      for (const chat of orderAnalysisChats) {
        const response = await runOrderSuggestions(chat.id);
        const data = response.data ?? {};
        summary.messagesScanned += Number(data.messages_scanned ?? 0);
        summary.documentsChecked += Number(data.documents_checked ?? 0);
        summary.missingDocumentsFound += Number(data.missing_documents_found ?? 0);
      }

      setGenerationSummary(summary);
      await loadSuggestions();
      requestAnimationFrame(() => {
        suggestionsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } catch {
      setGenerateSuggestionsError('Failed to generate suggestions.');
    } finally {
      setGeneratingSuggestions(false);
    }
  }, [loadSuggestions]);

  const itemsTotal = data.items.reduce(
    (sum, item) => sum + item.quantity * item.price,
    0,
  );
  const selectedSuggestion = suggestions.find((suggestion) => suggestion.id === activeSuggestionId) ?? suggestions[0] ?? null;

  // Progress indicator steps (exclude success)
  const progressSteps = visibleSteps.filter((s) => s !== 'success');

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div
        className={`grid grid-cols-1 gap-8 items-stretch lg:grid-cols-12 ${isRtl ? 'lg:[direction:ltr]' : ''}`}
      >
        {/* Left Sidebar: WhatsApp Context */}
        <div className="lg:col-span-4 xl:col-span-3" dir={isRtl ? 'rtl' : 'ltr'}>
          <WhatsAppSidebar
            chatName={generatingSuggestions ? 'AI Order Generation' : selectedChatName}
            messages={generatingSuggestions ? analysisMessages : selectedChatMessages}
            loading={generatingSuggestions ? false : fetchingMessages}
            groups={monitoredGroups}
            selectedGroupId={selectedChatId}
            generating={generatingSuggestions}
            highlightedMessageId={generatingSuggestions ? highlightedAnalysisMessageId : null}
            onLoadMore={activeSuggestionId ? undefined : loadMoreMessages}
            hasMore={activeSuggestionId ? false : hasMoreMessages}
            loadingMore={fetchingMoreMessages}
            waStatus={waStatus}
            waLoading={waLoading}
            onSelectGroup={(id, name) => {
              setSelectedChatId(id);
              setSelectedChatName(name);
              setActiveSuggestionId(null);
            }}
          />
        </div>

        {/* Right Content: Wizard */}
        <div className="lg:col-span-8 xl:col-span-9" dir={isRtl ? 'rtl' : 'ltr'}>
          {/* Header */}
          <h1 className="text-2xl font-bold mb-1">
            {t('createDocument.title', 'Create Document')}
          </h1>
          <p className="text-gray-500 mb-6 text-sm">
            {t('createDocument.subtitle', 'Quickly create invoices, receipts, and delivery notes')}
          </p>

          {/* Progress bar */}
          {step !== 'success' && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                {progressSteps.map((s, i) => {
                  const sIdx = visibleSteps.indexOf(s);
                  const isActive = sIdx === currentIndex;
                  const isDone = sIdx < currentIndex;
                  return (
                    <div key={s} className="flex items-center flex-1 last:flex-none">
                      <div className="flex flex-col items-center">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                            isActive
                              ? 'bg-blue-600 text-white'
                              : isDone
                                ? 'bg-green-500 text-white'
                                : 'bg-gray-200 text-gray-500'
                          }`}
                        >
                          {isDone ? '✓' : i + 1}
                        </div>
                        <span
                          className={`text-xs mt-1 hidden sm:block ${
                            isActive ? 'text-blue-600 font-medium' : 'text-gray-400'
                          }`}
                        >
                          {t(STEP_LABELS[s], s)}
                        </span>
                      </div>
                      {i < progressSteps.length - 1 && (
                        <div
                          className={`flex-1 h-0.5 mx-2 ${
                            sIdx < currentIndex ? 'bg-green-500' : 'bg-gray-200'
                          }`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step content */}
          <div className="min-h-[400px]">
            {step === 'select-type' && (
              <div className="space-y-5">
                <DocTypeSelector
                  selected={data.category}
                  onSelect={handleCategorySelect}
                  disabled={refDataLoading}
                />
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={handleGenerateSuggestions}
                    disabled={generatingSuggestions || waStatus !== 'connected'}
                    className="inline-flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-3 text-sm font-semibold text-emerald-800 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <span className="text-base" aria-hidden="true">🪄</span>
                    {generatingSuggestions ? 'AI Generating Suggestions…' : 'AI Generate Order Suggestions'}
                  </button>
                </div>
              </div>
            )}

            {step === 'select-customer' && (
              <CustomerStep
                selected={data.customer}
                onSelect={handleCustomerSelect}
                customers={customers}
                loading={customersLoading}
              />
            )}

            {step === 'select-items' && (
              <ItemsStep
                items={data.items}
                onChange={handleItemsChange}
                rivhitItems={rivhitItems}
                rivhitItemsLoading={rivhitItemsLoading}
                customer={data.customer}
              />
            )}

            {step === 'payments' && (
              <PaymentsStep
                payments={data.payments}
                onChange={handlePaymentsChange}
                paymentTypes={paymentTypes}
                totalAmount={itemsTotal}
              />
            )}

            {step === 'review' && (
              <ReviewStep
                data={data}
                onCommentsChange={handleCommentsChange}
                onSuccess={handleSuccess}
              />
            )}

            {step === 'success' && data.createdDoc && (
              <SuccessStep document={data.createdDoc} onReset={resetWizard} />
            )}
          </div>

          {/* Navigation buttons */}
          {step !== 'success' && step !== 'review' && (
            <div className="flex justify-between mt-8 pt-4 border-t">
              <button
                onClick={goBack}
                disabled={currentIndex === 0}
                className={`px-6 py-3 rounded-xl font-medium transition-colors ${
                  currentIndex === 0
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                ← {t('common.back', 'Back')}
              </button>

              <button
                onClick={goNext}
                disabled={!canGoNext()}
                className={`px-8 py-3 rounded-xl font-medium transition-colors ${
                  canGoNext()
                    ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {t('createDocument.next', 'Next')} →
              </button>
            </div>
          )}

          {step !== 'success' && waStatus === 'connected' && (
            <section ref={suggestionsSectionRef} className="mt-12 border-t pt-8">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900">Order Suggestions</h2>
                <p className="text-sm text-gray-500">Latest 10 open suggestions from monitored groups</p>
              </div>

              {generateSuggestionsError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {generateSuggestionsError}
                </div>
              )}

              {generatingSuggestions && (
                <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                  AI is scanning WhatsApp messages and recent documents. Follow the live generation in the left pane.
                </div>
              )}

              {generationSummary && !generatingSuggestions && (
                <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                  Scanned {generationSummary.messagesScanned} messages and {generationSummary.documentsChecked} recent documents. Found {generationSummary.missingDocumentsFound} suggestions.
                </div>
              )}

              {suggestionsLoading ? (
                <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-500">Loading suggestions…</div>
              ) : suggestions.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-500">
                  No open order suggestions.
                </div>
              ) : (
                <div className="space-y-6">
                  {suggestions.map((suggestion) => {
                    const isSelected = selectedSuggestion?.id === suggestion.id;
                    const isActive = activeSuggestionId === suggestion.id;
                    const draftItems = suggestionDrafts[suggestion.id] ?? buildEditableSuggestionItems(suggestion.items, rivhitItems);
                    return (
                      <div
                        key={suggestion.id}
                        onClick={() => {
                          if (activeSuggestionId === suggestion.id) {
                            setActiveSuggestionId(null);
                          } else {
                            setActiveSuggestionId(suggestion.id);
                          }
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            if (activeSuggestionId === suggestion.id) {
                              setActiveSuggestionId(null);
                            } else {
                              setActiveSuggestionId(suggestion.id);
                            }
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        className={`block w-full cursor-pointer rounded-2xl border bg-white p-5 text-left shadow-sm transition hover:border-blue-300 hover:shadow-md ${
                          isSelected ? 'border-blue-300 ring-2 ring-blue-100' : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-bold text-gray-900">{suggestion.customer_name}</h3>
                              {isSelected && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveSuggestionId(null);
                                  }}
                                  className="text-[10px] font-bold text-blue-600 uppercase tracking-wider hover:underline"
                                >
                                  (Clear Selection)
                                </button>
                              )}
                            </div>
                            <p className="text-xs text-gray-400">{formatSuggestionTimestamp(suggestion.detected_at)}</p>
                          </div>
                          <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">
                            {draftItems.length || suggestion.items.length} items
                          </span>
                        </div>

                        <div className="mt-4 space-y-2">
                          {(draftItems.length > 0 ? draftItems : suggestion.items).slice(0, 20).map((item, index) => (
                            <div key={`${suggestion.id}-${index}`} className="flex items-center justify-between gap-3 text-sm group/item">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                {isSelected && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeDraftItemAt(suggestion.id, index);
                                    }}
                                    className="shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-red-50 text-red-400 opacity-0 group-hover/item:opacity-100 hover:bg-red-100 hover:text-red-600 transition-all"
                                    title="Remove item"
                                  >
                                    ✕
                                  </button>
                                )}
                                <span className="truncate text-gray-700 font-medium">{item.product_name}</span>
                                {item.is_custom && (
                                  <span className="shrink-0 rounded bg-amber-50 px-1.5 text-[10px] font-bold text-amber-600 border border-amber-200 uppercase tracking-tighter">Unmapped</span>
                                )}
                              </div>
                              <div className="flex items-center gap-4 shrink-0 text-gray-500 tabular-nums">
                                <span className="font-semibold text-gray-900">{item.quantity} {item.unit}</span>
                                {item.price != null && (
                                  <span className="font-bold text-blue-600">₪{Number(item.price).toFixed(2)}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>

                        {isSelected && (
                          <SuggestionItemEditor
                            items={draftItems}
                            rivhitItems={rivhitItems}
                            customerId={suggestion.customer_id}
                            onChange={(items) =>
                              setSuggestionDrafts((current) => ({
                                ...current,
                                [suggestion.id]: items,
                              }))
                            }
                          />
                        )}

                        <div className="mt-4 flex items-center gap-2 text-xs text-gray-400">
                          <span>{suggestion.source_messages?.length ?? 0} WhatsApp messages</span>
                          {isActive && <span className="rounded-full bg-blue-50 px-2.5 py-1 font-semibold text-blue-700">In use</span>}
                        </div>

                        <div className="mt-5 flex flex-wrap items-center gap-3">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              applySuggestion(suggestion);
                            }}
                            disabled={actingSuggestionId === suggestion.id}
                            className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50 shadow-sm transition"
                          >
                            Use in Create Document
                          </button>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleDismissSuggestion(suggestion.id);
                            }}
                            disabled={actingSuggestionId === suggestion.id}
                            className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition"
                          >
                            {actingSuggestionId === suggestion.id ? 'Dismissing…' : 'Dismiss'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
